import { BaseSyntheticEvent, FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	Button,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	rem,
	useDisclosure,
} from '@bonusly/design-system';
import { useEventListener } from '@bonusly/events';

import { FeedbackEvents, User } from '../../lib';
import FeedbackForm from './feedback-template-form-content';
import LoadingContent from './feedback-template-loading-content';
import { useSubmitFeedbackTemplate } from './hooks/use-submit-feedback-template';

interface GiveFeedbackFromTemplateProps {
	recipient: User;
	templateQuestions: {
		question: string;
		placeholder: string;
	}[];
	persistedAnswers?: string[];
	feedbackId?: string;
}

export const GiveFeedbackFromTemplateModal: FC = () => {
	const { isOpen, onClose, onOpen } = useDisclosure();
	const { t } = useTranslation('feedback');
	const [templateAnswers, setTemplateAnswers] = useState(['', '', '']);
	const [templateQuestions, setTemplateQuestions] = useState([
		{
			question: '',
			placeholder: '',
		},
		{
			question: '',
			placeholder: '',
		},
		{
			question: '',
			placeholder: '',
		},
	]);
	const [recipient, setRecipient] = useState<User | null>(null);
	const [feedbackId, setFeedbackId] = useState<string | null>(null);

	useEventListener(FeedbackEvents.OpenFeedbackTemplateModal, (data: GiveFeedbackFromTemplateProps) => {
		setTemplateQuestions(data.templateQuestions);
		setRecipient(data.recipient);
		setTemplateAnswers(data.persistedAnswers || ['', '', '']);
		setFeedbackId(data.feedbackId || null);
		onOpen();
	});

	const onReset = () => {
		setTemplateAnswers(['', '', '']);
		onClose();
	};

	const handleAnswerChange =
		(index: number) =>
		(e: BaseSyntheticEvent): void => {
			const inputValue = e.target.value;

			setTemplateAnswers((prevAnswers) => {
				const updatedAnswers = [...prevAnswers];
				updatedAnswers[index] = inputValue;
				return updatedAnswers;
			});
		};

	const templateParsedAsObject = () => {
		return templateQuestions.reduce(
			(acc, question, index) => {
				acc[question.question] = templateAnswers[index];
				return acc;
			},
			{} as Record<string, string>
		);
	};

	const { isCreating, isUpdating, onCreateFeedback, onUpdateFeedback } = useSubmitFeedbackTemplate({
		template: templateParsedAsObject(),
		onClose,
		onReset,
	});

	const isLoading = feedbackId ? isUpdating : isCreating;

	if (!recipient) {
		return null;
	}

	const onSaveAsTemplate = (event: BaseSyntheticEvent) => {
		event.preventDefault();
		if (feedbackId) {
			onUpdateFeedback(feedbackId);
		} else {
			onCreateFeedback(recipient.id);
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside" size="2xl">
			<ModalOverlay />
			<ModalContent as="form">
				<ModalHeader borderBottomWidth={rem(1)} borderColor="gray.100" borderStyle="solid" color="gray.900">
					{t('feedback.give_modal.title')}
				</ModalHeader>
				<ModalCloseButton size="lg" borderRadius="full" />

				<ModalBody px={5} pt={5} pb={0}>
					{isLoading ? (
						<LoadingContent />
					) : (
						<FeedbackForm
							recipient={recipient}
							templateQuestions={templateQuestions}
							templateAnswers={templateAnswers}
							handleAnswerChange={handleAnswerChange}
						/>
					)}
				</ModalBody>
				{!isLoading && (
					<ModalFooter>
						<Box>
							<Button colorScheme="primary" mr={3} variant="alpha" onClick={onReset}>
								{t('feedback.give_modal.cancel_button')}
							</Button>

							<Button type="submit" colorScheme="primary" variant="solid" disabled={isLoading} onClick={onSaveAsTemplate}>
								{t('feedback.give_modal.next_button')}
							</Button>
						</Box>
					</ModalFooter>
				)}
			</ModalContent>
		</Modal>
	);
};
